import React, { useState, useMemo } from "react";
import {
  Search,
  Users,
  Briefcase,
  BarChart3,
  Crown,
  UserCheck,
  AlertCircle,
  Grid3X3,
  List,
  Layers,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../constants/appConfig";
import { ViewLayout } from "../components/ui/ViewLayout";
import { Card } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { FormSelect } from "../components/ui/FormControls";
import {
  ZoneOneHeader,
  ZoneThreeFooter,
} from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import { DonorCard } from "../components/donors/DonorCard";
import { DonorListTable } from "../components/donors/DonorListTable";
import { DonorDeckView } from "../components/donors/DonorDeckView";
import { HELP } from "../constants/helpContent";

export const DirectorPortfolioView = () => {
  const { palette, members, directors, goBack, goHome, pathways, navigate } = useApp();
  const [selectedDir, setSelectedDir] = useState("overview");
  const [portfolioSearch, setPortfolioSearch] = useState("");
  const [portfolioViewMode, setPortfolioViewMode] = useState("card");

  const portfolios = useMemo(() => {
    const map = {};
    directors.forEach((d) => {
      map[d.name] = { director: d, members: [], name: d.name };
    });
    const unassigned = [];
    members.forEach((member) => {
      const dir = member.assignedDirector?.trim();
      if (dir && map[dir]) map[dir].members.push(member);
      else if (!dir) unassigned.push(member);
    });
    return { byDirector: map, unassigned };
  }, [members, directors]);

  const directorNames = Object.keys(portfolios.byDirector).sort();

  const displayMembers = useMemo(() => {
    if (selectedDir === "overview") return [];
    let list =
      selectedDir === "unassigned"
        ? portfolios.unassigned
        : portfolios.byDirector[selectedDir]?.members || [];
    if (portfolioSearch) {
      const query = portfolioSearch.toLowerCase();
      list = list.filter((m) =>
        `${m.firstName} ${m.lastName} ${m.organization}`
          .toLowerCase()
          .includes(query)
      );
    }
    return list;
  }, [selectedDir, portfolios, portfolioSearch]);

  return (
    <ViewLayout>
      <ZoneOneHeader
        title="Director Portfolios"
        subtitle="Donor portfolio assignments"
        icon={Briefcase}
        helpContent={HELP.portfolios}
        onBack={goBack}
        onHome={goHome}
      />
      <ZoneTwoToolbar />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 24px",
          background: palette.toolbarBg,
          borderBottom: `1px solid ${palette.cardBorder}`,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <FormSelect
          value={selectedDir}
          onChange={setSelectedDir}
          options={[
            { value: "overview", label: "Overview" },
            ...directorNames.map((n) => ({
              value: n,
              label: `${n} (${portfolios.byDirector[n].members.length})`,
            })),
            {
              value: "unassigned",
              label: `Unassigned (${portfolios.unassigned.length})`,
            },
          ]}
        />
        {selectedDir !== "overview" && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", gap: 2 }}>
              {[
                { id: "card", icon: Grid3X3, label: "Cards" },
                { id: "list", icon: List, label: "List" },
                { id: "deck", icon: Layers, label: "Deck" },
              ].map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => setPortfolioViewMode(mode.id)}
                  title={mode.label}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "none",
                    cursor: "pointer",
                    background: portfolioViewMode === mode.id ? palette.accent1 : palette.inputBg,
                    color: portfolioViewMode === mode.id ? "#fff" : palette.text2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <mode.icon size={ICON_SIZES.sm} />
                </button>
              ))}
            </div>
            <div style={{ position: "relative", minWidth: 200 }}>
              <Search
                size={ICON_SIZES.sm}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: palette.text3,
                }}
              />
              <input
                value={portfolioSearch}
                onChange={(e) => setPortfolioSearch(e.target.value)}
                placeholder="Search portfolio..."
                style={{
                  padding: "7px 12px 7px 32px",
                  borderRadius: 8,
                  fontSize: FONT_SIZES.sm,
                  fontFamily: FONT_FAMILY,
                  outline: "none",
                  background: palette.inputBg,
                  color: palette.inputColor,
                  border: `1px solid ${palette.inputBorder}`,
                  width: "100%",
                }}
              />
            </div>
          </div>
        )}
      </div>
      <div style={{ flex: 1, padding: 24, overflowY: "auto" }}>
        {selectedDir === "overview" ? (
          <div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 14,
                marginBottom: 24,
              }}
            >
              {[
                {
                  label: "Directors",
                  value: directors.length,
                  icon: Crown,
                  color: palette.accent1,
                },
                {
                  label: "Assigned",
                  value: members.length - portfolios.unassigned.length,
                  icon: UserCheck,
                  color: "#10b981",
                },
                {
                  label: "Unassigned",
                  value: portfolios.unassigned.length,
                  icon: AlertCircle,
                  color:
                    portfolios.unassigned.length > 0 ? "#f59e0b" : "#10b981",
                },
                {
                  label: "Avg/Director",
                  value: directors.length
                    ? Math.round(
                        (members.length - portfolios.unassigned.length) /
                          directors.length
                      )
                    : 0,
                  icon: BarChart3,
                  color: palette.accent1,
                },
              ].map((stat, index) => (
                <Card key={index} hoverable style={{ padding: 16 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: FONT_SIZES.xxxl,
                          fontWeight: 700,
                          color: stat.color,
                        }}
                      >
                        {stat.value}
                      </div>
                      <div
                        style={{
                          fontSize: FONT_SIZES.sm,
                          color: palette.text2,
                          fontWeight: 500,
                        }}
                      >
                        {stat.label}
                      </div>
                    </div>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: `${stat.color}12`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <stat.icon size={ICON_SIZES.md} style={{ color: stat.color }} />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 16,
              }}
            >
              {directorNames.map((name) => {
                const portfolio = portfolios.byDirector[name];
                return (
                  <Card
                    key={name}
                    hoverable
                    onClick={() => setSelectedDir(name)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 12,
                      }}
                    >
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 12,
                          background: palette.background,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: FONT_SIZES.md,
                          fontWeight: 700,
                          color: "#fff",
                        }}
                      >
                        {name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: FONT_SIZES.md,
                            fontWeight: 600,
                            color: palette.text1,
                          }}
                        >
                          {name}
                        </div>
                        <div
                          style={{
                            fontSize: FONT_SIZES.sm,
                            color: palette.text2,
                          }}
                        >
                          {portfolio.members.length} assigned
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {pathways.map((pt) => {
                        const count = portfolio.members.filter(
                          (m) => m.pathway === pt.id
                        ).length;
                        return count > 0 ? (
                          <Badge
                            key={pt.id}
                            label={`${pt.name}: ${count}`}
                            color={pt.color}
                            small
                          />
                        ) : null;
                      })}
                    </div>
                  </Card>
                );
              })}
              {portfolios.unassigned.length > 0 && (
                <Card
                  hoverable
                  onClick={() => setSelectedDir("unassigned")}
                  style={{ borderColor: "#f59e0b40" }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        background: "#fef3c7",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AlertCircle size={ICON_SIZES.lg} style={{ color: "#f59e0b" }} />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: FONT_SIZES.md,
                          fontWeight: 600,
                          color: "#92400e",
                        }}
                      >
                        Unassigned
                      </div>
                      <div
                        style={{ fontSize: FONT_SIZES.sm, color: "#b45309" }}
                      >
                        {portfolios.unassigned.length} need a director
                      </div>
                    </div>
                  </div>
                </Card>
              )}
              {directors.length === 0 && (
                <Card
                  style={{
                    gridColumn: "1/-1",
                    textAlign: "center",
                    padding: 40,
                  }}
                >
                  <Crown
                    size={ICON_SIZES.xxl}
                    style={{ color: palette.text3, marginBottom: 12 }}
                  />
                  <h3 style={{ color: palette.text1, margin: "0 0 8px" }}>
                    No Directors Added
                  </h3>
                  <p
                    style={{
                      color: palette.text2,
                      fontSize: FONT_SIZES.sm,
                      margin: 0,
                    }}
                  >
                    Add directors in Settings to begin assigning donor
                    portfolios.
                  </p>
                </Card>
              )}
            </div>
          </div>
        ) : (
          <div>
            <h3
              style={{
                margin: "0 0 16px",
                fontSize: FONT_SIZES.lg,
                color: palette.textOnGrad,
              }}
            >
              {selectedDir === "unassigned"
                ? "Unassigned Donors"
                : `${selectedDir}'s Portfolio`}
              <span
                style={{
                  fontSize: FONT_SIZES.sm,
                  fontWeight: 400,
                  marginLeft: 12,
                  opacity: 0.8,
                }}
              >
                {displayMembers.length} donors
              </span>
            </h3>
            {displayMembers.length === 0 ? (
              <Card style={{ textAlign: "center", padding: 32 }}>
                <Users
                  size={ICON_SIZES.xxl}
                  style={{ color: palette.text3, marginBottom: 8 }}
                />
                <p
                  style={{
                    color: palette.text2,
                    fontSize: FONT_SIZES.sm,
                    margin: 0,
                  }}
                >
                  {portfolioSearch
                    ? "No matches"
                    : "No donors in this portfolio"}
                </p>
              </Card>
            ) : portfolioViewMode === "list" ? (
              <DonorListTable members={displayMembers} />
            ) : portfolioViewMode === "deck" ? (
              <DonorDeckView
                members={displayMembers}
                onMemberClick={(m) => navigate("memberDetail", m)}
              />
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
                  gap: 16,
                }}
              >
                {displayMembers.map((member) => (
                  <DonorCard key={member.id || member.afpId} member={member} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <ZoneThreeFooter />
    </ViewLayout>
  );
};
